"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.syncWithCis = void 0;
/* eslint-env browser */
var appNexus_1 = __importDefault(require("./appNexus"));
var cookie_1 = require("./cookie");
var cookie_config_1 = require("./cookie-config");
var crossReference_1 = __importDefault(require("./crossReference"));
var pulse_cookie_1 = require("./pulse-cookie");
var PULSE_COOKIE_KEY = cookie_config_1.cookieConfig.PULSE_COOKIE_KEY;
var USER_SERVICE_URL = 'https://cis.schibsted.com';
var USER_SERVICE_PATH = '/api/v1/identify';
/**
 * Call CIS with the current user information.
 *
 * @private
 */
function identify(_a) {
    var _b = _a === void 0 ? {} : _a, resolvedJwe = _b.resolvedJwe, userId = _b.userId, anId = _b.anId, environmentId = _b.environmentId, cisLocation = _b.cisLocation;
    var jwe = resolvedJwe;
    return fetch(cisLocation + USER_SERVICE_PATH, {
        method: "POST",
        headers: new Headers({
            "Content-Type": "application/json",
        }),
        body: JSON.stringify({
            jwe: jwe,
            userId: userId,
            anId: anId,
            environmentId: environmentId,
        }),
        credentials: "include",
    });
}
/** Identify recursively until no `redirectToHost` field */
function identifyWithRedirect(_a, redirects) {
    var resolvedJwe = _a.resolvedJwe, userId = _a.userId, anId = _a.anId, environmentId = _a.environmentId, cisLocation = _a.cisLocation;
    if (redirects === void 0) { redirects = 0; }
    if (redirects > 2) {
        return Promise.reject(new Error("Too many redirects"));
    }
    return identify({ resolvedJwe: resolvedJwe, userId: userId, anId: anId, environmentId: environmentId, cisLocation: cisLocation })
        .then(function (response) { return response.json(); })
        .then(function (_a) {
        var data = _a.data;
        if (data.redirectToHost) {
            return identifyWithRedirect({
                resolvedJwe: resolvedJwe,
                userId: userId,
                anId: anId,
                environmentId: environmentId,
                cisLocation: "https://".concat(data.redirectToHost),
            }, redirects + 1);
        }
        return Promise.resolve(data);
    });
}
var currentCisRequest = Promise.resolve();
var currentAppNexusRequest = Promise.resolve();
/**
 * Call CIS serially if needed, 'needed' being evaluated when the call reaches the top of the queue.
 *
 * @private
 */
function serialSyncWithCis(shortUserId, forceSync, cisLocation, nativeJwe, realm, reason) {
    if (forceSync === void 0) { forceSync = false; }
    currentCisRequest = currentCisRequest.then(function () {
        // Re-read our sync state as the previous cis request might have
        // made this sync obsolete.
        var cookie = (0, cookie_1.readCookie)(PULSE_COOKIE_KEY);
        var _a = (0, pulse_cookie_1.parsePulseCookies)(cookie), jwe = _a.jwe, anId = _a.anId, prevUserId = _a.prevUserId, environmentId = _a.environmentId;
        var state = (0, pulse_cookie_1.getSyncState)(shortUserId, { cookie: cookie });
        // For reason === login, we know shortUserId should be set and should be trusted.
        // For reason === logout, we know shortUserId is unset, but should be trusted.
        // For reason === initialize, we can't trust shortUserId and should use prevUserId instead.
        // For reason === refresh, we could use either as they should be in sync, but using
        // prevUserId makes it possible to do regular syncs without access to an event, so
        // we use that.
        var resolvedJwe = nativeJwe; // always send a nativeJwe when present. Hybrid app use case;
        var prominentUserId = shortUserId;
        if (reason !== pulse_cookie_1.CisSyncReason.Login && reason !== pulse_cookie_1.CisSyncReason.Logout) {
            resolvedJwe = nativeJwe || jwe;
            prominentUserId = prevUserId;
        }
        var userId = prominentUserId && "sdrn:".concat(realm, ":user:").concat(prominentUserId);
        if (state.syncWithCis || forceSync) {
            return identifyWithRedirect({
                resolvedJwe: resolvedJwe,
                userId: userId,
                anId: anId,
                environmentId: environmentId,
                cisLocation: cisLocation,
            }).then(function (data) {
                var result = __assign(__assign({}, data), { prevUserId: shortUserId });
                (0, pulse_cookie_1.serializePulseCookie)(result);
                (0, crossReference_1.default)(data.crossReference);
                return Promise.resolve(result);
            });
        }
        return Promise.resolve((0, pulse_cookie_1.parsePulseCookies)(cookie));
    });
    return currentCisRequest;
}
/**
 * Sync with CIS
 *
 * This method deals with all communication and communication with dependent systems (currently AppNexus and Norstat).
 *
 * An optional user id should be provided if a tracker have received a logged in state. Providing a new user id
 * (different than a previous user id, serialized to the pulse cookie) will cause a "blocking call" to CIS (the
 * returned promise will not resolve before the communication is done). This is done because CIS could provide new data
 * regarding opt out.
 *
 * The returned promise will also be blocking if no pulse cookie is found. Pulse requires environmentIds generated
 * server side so one have to wait to be allocated to one.
 *
 * In other context CIS could or could not be called but the returned promise will resolve immediately. If CIS have
 * been called recently there is not point to call it again. In a different case, if a refresh call to CIS is required
 * it doesn't mean that other systems have to wait of it. Both cases will return a promise that resolves more or less
 * immediately.
 *
 * @returns {Promise<PulseCookie>} Events should be blocked until the returned promise
 * resolves.
 *
 */
function syncWithCis(shortUserId, nativeJwe, userServiceLocation, realm, investigateSyncOn, xandrEnabled) {
    if (realm === void 0) { realm = 'schibsted.com'; }
    if (xandrEnabled === void 0) { xandrEnabled = true; }
    var _a = (0, pulse_cookie_1.getSyncState)(shortUserId, { investigateSyncOn: investigateSyncOn }), syncWithXandr = _a.syncWithAppNexus, _syncWithCis = _a.syncWithCis, waitForCis = _a.waitForCis, syncWithCisReason = _a.syncWithCisReason;
    var cisLocation = userServiceLocation || USER_SERVICE_URL;
    var cisRequest = _syncWithCis
        ? serialSyncWithCis(shortUserId, false, cisLocation, nativeJwe, realm, syncWithCisReason)
        : Promise.resolve();
    // If `waitForCis` is false the update with CIS is only a "soft sync" and
    // shouldn't block other operations. This is the case when the browser has a
    // _pulse2data cookie and the user haven't logged in or out. When the user do
    // log in we have to wait to avoid sending events with user ids after logging
    // out or without ids after logging in.
    var syncProgress = waitForCis
        ? cisRequest.then(function (requestData) {
            var cookie = (0, pulse_cookie_1.parsePulseCookies)();
            return requestData || cookie;
        })
        : Promise.resolve((0, pulse_cookie_1.parsePulseCookies)());
    if (xandrEnabled && syncWithXandr) {
        // There is not a strict dependency between AppNexus and CIS here, but
        // AppNexus can send a 302 to CIS. We want to wait for the initial CIS
        // handshake to set all cookies on schibsted.com before letting AppNexus
        // redirect there (potentially setting new cookies)
        currentAppNexusRequest = currentAppNexusRequest
            .then(function () { return syncProgress; }) // Wait for any other AppNexus request
            .then(function () { return (0, appNexus_1.default)(cisLocation); }) // Wait for current AppNexus request
            .then(function (resyncWithCis) {
            // Syncing with AppNexus could have resulted in a new anId that
            // have to be synchronized with CIS.
            if (resyncWithCis) {
                return serialSyncWithCis(shortUserId, true, cisLocation, nativeJwe, realm, pulse_cookie_1.CisSyncReason.NewAnid); // wait for CIS if needed
            }
            return Promise.resolve();
        });
    }
    return syncProgress;
}
exports.syncWithCis = syncWithCis;
//# sourceMappingURL=cis-browser.js.map